import React from 'react'
import { Row, Col, Button, Card, CardHeader, CardTitle, CardBody } from 'reactstrap'
import {
  Alert,
  AlertTitle,
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import moment from 'moment'
import 'moment/locale/fi'
import ReactTable from '../../components/Lists/ReactTableWrapper'
import { withFirebase } from '../../components/Fb'
import FalconLoading from '../../components/Inputs/FalconLoading'
import {
  CONFIRMATION_STATES,
  MIN_CHOICES,
  SPARE_GRACE_HOURS,
  choiceStatus,
  confirmationLabel,
  confirmationState,
  pendingSoon,
  reservationIdsOf,
  resourceNames,
  rubberRows,
  rubberTally,
  spareHoursLeft,
  staleSpares,
  tallyLabel,
} from '../../components/utils/teamties'
import { teamQuota } from '../../components/utils/teams'

// Kohtaamiset (Teams / sarjaottelut). docs/features/teams.md §4.4 and Phase D.
//
// Oversight, not booking. Everything here reads; the two write actions that change bookings
// (cancel a tie, resend the links) are Phase E callables, because releasing quota and cancelling
// the remaining rubbers has to happen together and a client cannot be trusted with that.
//
// The tally and every time and court on this screen are joined LIVE from the reservations, never
// read off the tie. §4.4 stores no times precisely so that an admin dragging a court booking in
// the calendar cannot leave this screen showing yesterday's time - which means the join is the
// feature, not an optimisation to remove later.

const PENDING_HORIZON_DAYS = 14

const fmtDate = (iso) => (iso ? moment(iso, 'YYYY-MM-DD').format('dd D.M.YYYY') : '')
const fmtTime = (ts) => (ts ? moment.utc(ts).format('D.M. HH:mm') : '')

const stateColor = (state) => (state === 'confirmed' ? 'success' : 'warning')

// The actual opponent, which only exists once someone has claimed the date: it is what the
// opponent typed on the public acceptance page. The tie's own `opponent` field is NOT this - it
// is the manager's private note and may well read "soitettu, ei vastausta".
const opponentTeam = (tie) => (tie && tie.confirmation && tie.confirmation.opponentteam) || ''

class TeamTiesContainer extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      loading: true,
      loaderror: null,
      ties: [],
      teams: [],
      formats: [],
      seasons: [],
      reservations: {},
      seasonfilter: 'all',
      openTie: null,
      confirmEdit: null,
      working: false,
      actionerror: null,
      actionnotice: null,
    }
  }

  componentDidMount() {
    this.loadData()
  }

  loadData = async () => {
    try {
      const [tieSnap, teamSnap, formatSnap, seasonSnap] = await Promise.all([
        this.props.firebase.getTeamTies(),
        this.props.firebase.getTeams(),
        this.props.firebase.getTeamFormats(),
        this.props.firebase.getTeamSeasons(),
      ])
      const alive = (snap) =>
        snap.empty ? [] : snap.docs.map((d) => ({ ...d.data(), id: d.id })).filter((x) => x.state !== 'deleted')
      // Cancelled ties are NOT filtered out here. A venue asking "what happened to that
      // fixture" needs to see that it was cancelled rather than find it missing.
      const ties = (tieSnap.empty ? [] : tieSnap.docs.map((d) => ({ ...d.data(), id: d.id }))).sort((a, b) =>
        String(b.date || '').localeCompare(String(a.date || ''))
      )

      const reservations = await this.loadReservations(ties)

      this.setState({
        ties,
        teams: alive(teamSnap),
        formats: alive(formatSnap),
        seasons: alive(seasonSnap),
        reservations,
        loading: false,
        loaderror: null,
      })
    } catch (error) {
      console.error('Loading team ties failed', error)
      this.setState({ loading: false, loaderror: error.message })
    }
  }

  // One read per distinct rubber reservation. §4.4 calls four extra reads a cheap price for
  // never showing a stale booking, and this is where that price is paid. A reservation that has
  // gone entirely is left out of the map, which is what makes rubberStatus report 'missing'.
  loadReservations = async (ties) => {
    const ids = reservationIdsOf(ties)
    if (!ids.length) return {}
    const snaps = await Promise.all(ids.map((id) => this.props.firebase.getReservation(id).catch(() => null)))
    const map = {}
    snaps.forEach((snap, i) => {
      if (snap && snap.exists) map[ids[i]] = { ...snap.data(), id: ids[i] }
    })
    return map
  }

  teamById = (id) => this.state.teams.find((t) => t.id === id) || null

  formatById = (id) => this.state.formats.find((f) => f.id === id) || null

  // The tie records the format it was booked under, which is the one to derive the offer from
  // even if the team has since been moved to another (§4.4).
  formatOfTie = (tie) => {
    const byTie = tie && tie.formatid ? this.formatById(tie.formatid) : null
    if (byTie) return byTie
    const team = this.teamById(tie && tie.teamid)
    return team ? this.formatById(team.formatid) : null
  }

  visibleTies = () => {
    if (this.state.seasonfilter === 'all') return this.state.ties
    return this.state.ties.filter((t) => t.seasonid === this.state.seasonfilter)
  }

  tallyOf = (tie) => rubberTally(tie, this.formatOfTie(tie), this.state.reservations)

  tableRows = () =>
    this.visibleTies().map((tie) => {
      const team = this.teamById(tie.teamid)
      return {
        id: tie.id,
        // teamname is denormalised on the tie (§4.4) but the live team wins when it is there,
        // so a renamed team does not read as two different teams across the list.
        team: (team && team.name) || tie.teamname || '(poistettu joukkue)',
        date: fmtDate(tie.date),
        opponent: opponentTeam(tie) || (confirmationState(tie) === 'confirmed' ? '(ei ilmoitettu)' : '—'),
        note: tie.opponent || '',
        confirmation: confirmationLabel(tie),
        tally: tallyLabel(this.tallyOf(tie)),
        statetext: tie.state === 'cancelled' ? 'Peruttu' : 'Voimassa',
      }
    })

  openTie = (id) => {
    const tie = this.state.ties.find((t) => t.id === id)
    if (tie) this.setState({ openTie: tie, actionerror: null, actionnotice: null })
  }

  closeTie = () => this.setState({ openTie: null, actionerror: null, actionnotice: null })

  askConfirmEdit = (tie) => {
    this.setState({
      confirmEdit: {
        tieid: tie.id,
        state: confirmationState(tie),
        opponentteam: (tie.confirmation && tie.confirmation.opponentteam) || '',
      },
    })
  }

  saveConfirmation = async () => {
    const edit = this.state.confirmEdit
    if (!edit) return
    this.setState({ working: true, actionerror: null })
    try {
      await this.props.firebase.setTeamTieConfirmation(edit)
      await this.loadData()
      this.setState({ working: false, confirmEdit: null, openTie: null })
    } catch (error) {
      console.error('Saving tie confirmation failed', error)
      this.setState({ working: false, actionerror: error.message })
    }
  }

  cancelTie = async (tie) => {
    this.setState({ working: true, actionerror: null, actionnotice: null })
    const result = await this.props.firebase.cancelTeamTie(tie.id)
    if (result && result.error) {
      // Phase E has not shipped, so this is the expected answer for now. Say which function is
      // missing rather than showing a bare Firebase error.
      this.setState({
        working: false,
        actionerror:
          'Kohtaamisen peruminen vaatii teams-canceltie -funktion, jota ei ole vielä julkaistu. ' +
          'Peru toistaiseksi kohtaamisen vuorot kalenterista. (' + result.message + ')',
      })
      return
    }
    await this.loadData()
    this.setState({ working: false, openTie: null, actionnotice: 'Kohtaaminen peruttu.' })
  }

  resendLinks = async (tie) => {
    this.setState({ working: true, actionerror: null, actionnotice: null })
    const result = await this.props.firebase.resendTeamTieLinks(tie.id)
    if (result && result.error) {
      this.setState({
        working: false,
        actionerror:
          'Linkin lähetys vaatii teams-resendlinks -funktion, jota ei ole vielä julkaistu. (' +
          result.message + ')',
      })
      return
    }
    this.setState({ working: false, actionnotice: 'Linkki lähetetty joukkueenjohtajalle.' })
  }

  // --- the two lists the venue will ask for in week one --------------------

  renderAttentionLists = () => {
    const ties = this.visibleTies()
    const spares = []
    this.state.teams.forEach((team) => {
      staleSpares(team, ties).forEach((tie) => spares.push({ team, tie }))
    })
    // The league's two-choice rule (see utils/teamties.js). Broken almost always means a tie or
    // its courts were cancelled after the manager had already mailed the links, so it is the
    // venue - not the manager - who is in a position to notice and put it right.
    const shortofchoices = this.state.teams
      .map((team) => ({ team, status: choiceStatus(team, ties) }))
      .filter(({ status }) => status && status.remaining && !status.ok)
    const soon = pendingSoon(ties, moment().format('YYYY-MM-DD'), PENDING_HORIZON_DAYS)
    if (!spares.length && !soon.length && !shortofchoices.length) return null
    return (
      <>
        {shortofchoices.length ? (
          <Alert severity='error' sx={{ mb: 2 }}>
            <AlertTitle>Liian vähän vaihtoehtoja vastustajille ({shortofchoices.length})</AlertTitle>
            <Typography variant='body2' sx={{ mb: 1 }}>
              Sarjasääntö vaatii, että jokaiselle vastustajalle tarjotaan vähintään{' '}
              {MIN_CHOICES} vaihtoehtoa. Näillä joukkueilla varauksia on peruttu niin monta, ettei
              se enää toteudu — viimeinen vastustaja saisi vain yhden päivän. Lisää kohtaaminen
              tai palauta peruttu.
            </Typography>
            {shortofchoices.map(({ team, status }) => (
              <Typography key={team.id} variant='body2'>
                <b>{team.name}</b> — {status.unclaimed} vapaata päivää, {status.remaining} vastustajaa
                vastaamatta; tarvitaan {status.needed}
              </Typography>
            ))}
          </Alert>
        ) : null}
        {spares.length ? (
          <Alert severity='warning' sx={{ mb: 2 }}>
            <AlertTitle>Vapautuvia vuoroja ({spares.length})</AlertTitle>
            <Typography variant='body2' sx={{ mb: 1 }}>
              Näillä joukkueilla on jo tarvittavat vahvistetut kotiottelut, eikä yhtään
              vastustajaa ole enää tulossa tälle päivälle. Joukkueenjohtajalla on {SPARE_GRACE_HOURS} h
              aikaa perua vuoro itse, minkä jälkeen järjestelmä vapauttaa sen.
            </Typography>
            {spares.map(({ team, tie }) => {
              const left = spareHoursLeft(team, ties)
              return (
                <Typography key={tie.id} variant='body2'>
                  <b>{team.name}</b> — {fmtDate(tie.date)}
                  {tie.opponent ? ' (' + tie.opponent + ')' : ''} — {confirmedCountLabel(team, ties)} —{' '}
                  {spareCountdown(left)}
                </Typography>
              )
            })}
          </Alert>
        ) : null}
        {soon.length ? (
          <Alert severity='info' sx={{ mb: 2 }}>
            <AlertTitle>
              Vahvistamattomia kohtaamisia seuraavan {PENDING_HORIZON_DAYS} päivän sisällä ({soon.length})
            </AlertTitle>
            {soon.map((tie) => {
              const team = this.teamById(tie.teamid)
              return (
                <Typography key={tie.id} variant='body2'>
                  {fmtDate(tie.date)} — <b>{(team && team.name) || tie.teamname}</b>
                  {tie.opponent ? ' — muistiinpano: ' + tie.opponent : ''}
                </Typography>
              )
            })}
          </Alert>
        ) : null}
      </>
    )
  }

  renderTieDialog = () => {
    const tie = this.state.openTie
    if (!tie) return null
    const format = this.formatOfTie(tie)
    const team = this.teamById(tie.teamid)
    const rows = rubberRows(tie, format, this.state.reservations)
    const tally = this.tallyOf(tie)
    return (
      <Dialog open={!!tie} onClose={this.closeTie} maxWidth='md' fullWidth>
        <DialogTitle>
          {(team && team.name) || tie.teamname} — {fmtDate(tie.date)}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Stack direction='row' spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
              <Chip size='small' color={stateColor(confirmationState(tie))} label={confirmationLabel(tie)} />
              <Chip size='small' label={tallyLabel(tally)} />
              {tie.state === 'cancelled' ? <Chip size='small' color='error' label='Peruttu' /> : null}
            </Stack>

            <Box>
              <Typography variant='body2' color='text.secondary'>
                Vastustaja
              </Typography>
              <Typography variant='body1'>
                {opponentTeam(tie) ||
                  (confirmationState(tie) === 'confirmed'
                    ? '(vahvistettu, nimeä ei ilmoitettu)'
                    : 'Ei vielä vahvistettu — kukaan ei ole varannut tätä päivää')}
              </Typography>
              <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
                Joukkueenjohtajan muistiinpano
              </Typography>
              <Typography variant='body1'>{tie.opponent || '(ei merkintää)'}</Typography>
            </Box>

            <Divider />

            <Box>
              <Typography variant='body2' gutterBottom>
                Ottelut
              </Typography>
              {rows.length ? (
                rows.map((row, i) => (
                  <Box
                    key={row.reservationid || i}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: 2,
                      py: 0.75,
                      borderBottom: 1,
                      borderColor: 'divider',
                    }}
                  >
                    <Box>
                      <Typography variant='body2'>
                        <b>{row.typelabel}</b>
                        {row.duration ? ' ' + row.duration + ' min' : ''}
                      </Typography>
                      <Typography variant='body2' color='text.secondary'>
                        {row.status === 'booked'
                          ? fmtTime(row.start) + ' — ' + resourceNames(row.resources)
                          : row.status === 'cancelled'
                          ? 'Varaus peruttu kalenterissa'
                          : 'Varausta ei löydy'}
                      </Typography>
                    </Box>
                    {row.status !== 'booked' ? (
                      <Chip size='small' color='error' label='Ei voimassa' />
                    ) : null}
                  </Box>
                ))
              ) : (
                <Typography variant='body2' color='text.secondary'>
                  Joukkueenjohtaja ei ole vielä varannut yhtään ottelua tähän kohtaamiseen.
                </Typography>
              )}
              {tally.gone ? (
                <Alert severity='warning' sx={{ mt: 1 }}>
                  {tally.gone} ottelun varaus on peruttu tai poistettu kalenterista. Kohtaaminen
                  luulee pitävänsä niitä — kerro joukkueenjohtajalle, jos vuoroja ei ole tarkoitus
                  varata uudelleen.
                </Alert>
              ) : null}
            </Box>

            {this.state.actionerror ? <Alert severity='error'>{this.state.actionerror}</Alert> : null}
            {this.state.actionnotice ? <Alert severity='success'>{this.state.actionnotice}</Alert> : null}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between' }}>
          <Box>
            {tie.state !== 'cancelled' ? (
              <Button color='danger' onClick={() => this.cancelTie(tie)} disabled={this.state.working}>
                Peru kohtaaminen
              </Button>
            ) : null}
          </Box>
          <Box>
            <Button onClick={() => this.resendLinks(tie)} disabled={this.state.working}>
              Lähetä linkki uudelleen
            </Button>{' '}
            <Button onClick={() => this.askConfirmEdit(tie)} disabled={this.state.working}>
              Merkitse vahvistus
            </Button>{' '}
            <Button color='secondary' onClick={this.closeTie} disabled={this.state.working}>
              Sulje
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    )
  }

  renderConfirmEditDialog = () => {
    const edit = this.state.confirmEdit
    if (!edit) return null
    return (
      <Dialog open={!!edit} onClose={() => this.setState({ confirmEdit: null })} maxWidth='sm' fullWidth>
        <DialogTitle>Merkitse vahvistus käsin</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <Typography variant='body2' color='text.secondary'>
              Käytä tätä kun vastustaja ilmoittaa puhelimitse. Merkintä kirjataan ylläpidon
              tekemäksi, ei vastustajan itsensä.
            </Typography>
            <FormControl variant='standard' fullWidth>
              <InputLabel>Tila</InputLabel>
              <Select
                value={edit.state}
                onChange={(e) => this.setState({ confirmEdit: { ...edit, state: e.target.value } })}
              >
                {CONFIRMATION_STATES.map((s) => (
                  <MenuItem key={s.const} value={s.const}>
                    {s.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label='Vastustajajoukkue'
              value={edit.opponentteam}
              onChange={(e) => this.setState({ confirmEdit: { ...edit, opponentteam: e.target.value } })}
              variant='standard'
              fullWidth
            />
            {this.state.actionerror ? <Alert severity='error'>{this.state.actionerror}</Alert> : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => this.setState({ confirmEdit: null })} disabled={this.state.working}>
            Peruuta
          </Button>
          <Button color='success' onClick={this.saveConfirmation} disabled={this.state.working}>
            {this.state.working ? 'Tallennetaan...' : 'Tallenna'}
          </Button>
        </DialogActions>
      </Dialog>
    )
  }

  render() {
    if (this.state.loading) {
      return <FalconLoading />
    }
    return (
      <>
        <div className='content'>
          <Card>
            <CardHeader>
              <Row>
                <Col lg='10' md='5' sm='5'>
                  <CardTitle tag='h5'>Kohtaamiset</CardTitle>
                </Col>
              </Row>
            </CardHeader>
            <CardBody>
              {this.state.loaderror ? (
                <Alert severity='error' sx={{ mb: 2 }}>
                  Kohtaamisten lataus epäonnistui: {this.state.loaderror}
                </Alert>
              ) : null}
              {this.renderAttentionLists()}
              <Row>
                <Col md='4'>
                  <FormControl variant='standard' fullWidth>
                    <InputLabel>Kausi</InputLabel>
                    <Select
                      value={this.state.seasonfilter}
                      onChange={(e) => this.setState({ seasonfilter: e.target.value })}
                    >
                      <MenuItem value='all'>Kaikki kaudet</MenuItem>
                      {this.state.seasons.map((s) => (
                        <MenuItem key={s.id} value={s.id}>
                          {s.name || '(nimetön)'}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Col>
              </Row>
              <Row>
                <Col md='12'>
                  <ReactTable
                    data={this.tableRows()}
                    noSumRow={true}
                    columns={[
                      { Header: 'Joukkue', accessor: 'team' },
                      { Header: 'Päivä', accessor: 'date', width: 140 },
                      { Header: 'Vastustaja', accessor: 'opponent' },
                      { Header: 'Muistiinpano', accessor: 'note' },
                      { Header: 'Vahvistus', accessor: 'confirmation', width: 170 },
                      { Header: 'Ottelut', accessor: 'tally', width: 170 },
                      { Header: 'Tila', accessor: 'statetext', width: 110 },
                    ]}
                    onRowClick={(params) => this.openTie(params.row.id)}
                  />
                </Col>
              </Row>
            </CardBody>
          </Card>
          {this.renderTieDialog()}
          {this.renderConfirmEditDialog()}
        </div>
      </>
    )
  }
}

// The countdown the manager is working against. An overdue release is shown as overdue rather
// than as "0 h left", because it means the Phase E job has not run and the venue is still
// holding a court nobody is going to take.
const spareCountdown = (hoursleft) => {
  if (hoursleft === null) return 'vapautusaika ei tiedossa'
  if (hoursleft < 0) return 'vapautus myöhässä ' + Math.abs(Math.round(hoursleft)) + ' h'
  return 'vapautuu ' + Math.max(0, Math.round(hoursleft)) + ' h kuluttua'
}

// "2/3 vahvistettu, oikeus 4" - why this team's spare is now surplus.
const confirmedCountLabel = (team, ties) => {
  const confirmed = ties.filter(
    (t) => t.teamid === team.id && t.state !== 'cancelled' && confirmationState(t) === 'confirmed'
  ).length
  return confirmed + '/' + team.homematches + ' vahvistettu, oikeus ' + teamQuota(team)
}

export default withFirebase(TeamTiesContainer)
